
import { GoogleGenAI } from "@google/genai";

export type AppPersona = 'CORPORATE' | 'ENTHUSIASTIC' | 'GRUMPY' | 'SATIRE';
export type TimeFrame = 'day' | 'week' | 'month' | 'year';

const PERSONA_INSTRUCTIONS: Record<AppPersona, string> = {
  CORPORATE: `Act as a High-Level Strategic Communications Director. Use formal, jargon-heavy corporate double-speak.`,
  ENTHUSIASTIC: `Act as an Overly Enthusiastic Office Happiness Coach. Use extreme positivity and excitement.`,
  GRUMPY: `Act as a cynical, tired site custodian. Complain about everything being broken or pointless.`,
  SATIRE: `Act as a philosophical satirist treating parking as a grand existential drama.`
};

// Create the AI instance inside a getter to ensure process.env is available
const getAI = () => {
  const apiKey = process.env.GEMINI_API_KEY;
  if (!apiKey) {
    throw new Error("GEMINI_API_KEY is not defined in the environment");
  }
  return new GoogleGenAI({ apiKey });
};

export async function getParkingSummary(stats: any, persona: AppPersona = 'CORPORATE') {
  try {
    const ai = getAI();
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Current Parking Statistics: ${JSON.stringify(stats)}.`,
      config: {
        systemInstruction: `${PERSONA_INSTRUCTIONS[persona]} 
        Provide a summarisation of the current parking situation. 
        REQUIRED: Use **Sentence case** (no all-caps). 
        REQUIRED: Use **bolding** ONLY for numeric statistics (e.g., **30**, **5%**, **12**). 
        REQUIRED: DO NOT use any emojis or smileys in the text itself.
        Limit to 2 sentences.`,
        temperature: 0.9,
      }
    });
    return response.text;
  } catch (error) {
    console.error("Gemini Error:", error);
    return "The system is currently undergoing maintenance. There are **0** updates at this time.";
  }
}

export async function getAdminInsightReport(reportData: any, persona: AppPersona = 'CORPORATE', timeframe: TimeFrame = 'day') {
  try {
    const ai = getAI();
    const response = await ai.models.generateContent({
      model: 'gemini-3.1-pro-preview',
      contents: `Detailed Operational Data: ${JSON.stringify(reportData)}. Analysis Timeframe: ${timeframe}.`,
      config: {
        systemInstruction: `${PERSONA_INSTRUCTIONS[persona]} 
        Analyse the data and provide a tactical report focussed on the ${timeframe} timeframe. 
        If the timeframe is 'year', focus on macro-level stability and long-term planning. 
        If 'day', focus on immediate operational friction.
        REQUIRED: Use **Sentence case**. 
        REQUIRED: Use **bolding** ONLY for numeric data or percentages.
        REQUIRED: DO NOT use any emojis in the text.
        Provide 3 bullet points:
        1. One observation about current trends for this ${timeframe} with your specific voice.
        2. One bottleneck or problem relevant to this timeframe described through your character's lens.
        3. One 'game-changing' or 'bitter' recommendation for the future.`,
        temperature: 0.8,
      }
    });
    return response.text;
  } catch (error) {
    console.error("Gemini Insight Error:", error);
    return "Analytics are unavailable due to a technical glitch.";
  }
}

export async function getImprovementInsights(reportData: any, persona: AppPersona = 'CORPORATE') {
  try {
    const ai = getAI();
    const response = await ai.models.generateContent({
      model: 'gemini-3.1-pro-preview',
      contents: `Current application operational state: ${JSON.stringify(reportData)}.`,
      config: {
        systemInstruction: `Act as a world-class Senior Product Manager and UX Architect. 
        Your goal is to provide plain English suggestions to the developer on how to improve this car parking application.
        Focus on:
        1. New feature ideas (e.g. carpooling rewards, push notifications, gate hardware integration).
        2. UI/UX enhancements (e.g. accessibility, better mobile layouts, visual cues).
        3. Process efficiencies (e.g. automated penalty systems, fairer queue weighting).
        
        REQUIRED: Use plain, conversational English. 
        REQUIRED: Do not use corporate buzzwords or character personas for this section.
        REQUIRED: Use **Sentence case**.
        REQUIRED: Format as a list of 4-5 clear, actionable bullet points.`,
        temperature: 0.7,
      }
    });
    return response.text;
  } catch (error) {
    console.error("Gemini Improvement Error:", error);
    return "Improvement suggestions are currently unavailable.";
  }
}
